import { baseApi } from '@alltrails/redux-helpers/baseApi';
import type Waypoint from '@alltrails/types/Waypoint';
import type { WaypointId } from '@alltrails/types/Waypoint';
import type { UpdateWaypointPayload } from '../api/editWaypoints';
import type { WaypointChangesResponse } from '../types/WaypointsApi';

type GetUserWaypointsParams = {
  /** When true, only the user's global waypoints are returned. */
  globalOnly?: boolean;
};

type AddUserWaypointParams = {
  atMapId: number;
  waypoint: UpdateWaypointPayload;
};

type UpdateUserWaypointParams = {
  atMapId: number;
  waypointId: WaypointId;
  changes: UpdateWaypointPayload;
};

type DeleteUserWaypointParams = {
  atMapId: number;
  waypointId: WaypointId;
};

/**
 * NOTE: the `getUserWaypoints` query is the user's global waypoint list. The `addWaypoint` /
 * `updateWaypoint` / `deleteWaypoint` mutations hit a generic per-map endpoint and are also used
 * for waypoints attached to a recording (see `useRecordingWaypointEditing`); callers there manage
 * their own slice patches since the optimistic update here only touches the global cache.
 *
 * In-progress custom routes still flow through `customRoutesApi`'s bulk save so the route +
 * waypoints stay an atomic unit.
 */
export const waypointsApi = baseApi.enhanceEndpoints({ addTagTypes: ['UserWaypoint'] }).injectEndpoints({
  endpoints: builder => ({
    /**
     * https://alpha.mostpaths.com/api/docs#tag/My/operation/MyWaypoints
     */
    getUserWaypoints: builder.query<Waypoint[], GetUserWaypointsParams>({
      query: ({ globalOnly }) => ({
        url: '/api/alltrails/my/waypoints',
        method: 'get',
        params: globalOnly ? { global_only: true } : undefined
      }),
      transformResponse: (response: { waypoints: Waypoint[] }) => response.waypoints ?? [],
      providesTags: result => [
        ...(result ?? []).map(waypoint => ({ type: 'UserWaypoint' as const, id: waypoint.id })),
        { type: 'UserWaypoint' as const, id: 'LIST' }
      ]
    }),
    addWaypoint: builder.mutation<Waypoint, AddUserWaypointParams>({
      query: ({ atMapId, waypoint }) => ({
        url: `/api/alltrails/maps/${atMapId}/waypoints`,
        method: 'post',
        data: waypoint
      }),
      transformResponse: (response: WaypointChangesResponse) => response.waypoints[0],
      invalidatesTags: [{ type: 'UserWaypoint', id: 'LIST' }]
    }),
    updateWaypoint: builder.mutation<Waypoint, UpdateUserWaypointParams>({
      query: ({ atMapId, waypointId, changes }) => ({
        url: `/api/alltrails/maps/${atMapId}/waypoints/${waypointId}`,
        method: 'put',
        data: changes
      }),
      transformResponse: (response: WaypointChangesResponse) => response.waypoints[0],
      // Optimistically patch the cached `getUserWaypoints` list so the pin doesn't briefly snap back to
      // the pre-save server values between the mutation completing and the tag-driven refetch landing.
      // Rolled back if the request fails — invalidation will then refetch server truth.
      async onQueryStarted({ waypointId, changes }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          waypointsApi.util.updateQueryData('getUserWaypoints', { globalOnly: true }, draft => {
            const wp = draft.find(w => w.id === waypointId);
            if (!wp) {
              return;
            }
            if (changes.name !== undefined) {
              wp.name = changes.name;
            }
            if (changes.description !== undefined) {
              wp.description = changes.description;
            }
            if (changes.latitude !== undefined && changes.longitude !== undefined) {
              wp.location = { ...wp.location, latitude: changes.latitude, longitude: changes.longitude };
            }
            if (changes.showTitle !== undefined) {
              wp.waypointDisplayProperty = { ...(wp.waypointDisplayProperty ?? {}), showTitle: changes.showTitle };
            }
            if (changes.contentPrivacy !== undefined) {
              wp.contentPrivacy = changes.contentPrivacy;
            }
            if (changes.isGlobal !== undefined) {
              wp.isGlobal = changes.isGlobal;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { waypointId }) => [
        { type: 'UserWaypoint', id: waypointId },
        { type: 'UserWaypoint', id: 'LIST' }
      ]
    }),
    deleteWaypoint: builder.mutation<void, DeleteUserWaypointParams>({
      query: ({ atMapId, waypointId }) => ({
        url: `/api/alltrails/maps/${atMapId}/waypoints/${waypointId}`,
        method: 'delete'
      }),
      invalidatesTags: (_result, _error, { waypointId }) => [
        { type: 'UserWaypoint', id: waypointId },
        { type: 'UserWaypoint', id: 'LIST' }
      ]
    })
  })
});

export const { useGetUserWaypointsQuery, useAddWaypointMutation, useUpdateWaypointMutation, useDeleteWaypointMutation } = waypointsApi;
